import {MetricsService} from './../../../../shared/services/metrics.service';
import {Injectable} from '@angular/core';
import {RestApiService} from '../../../../shared/services/rest-api.service';
import {SessionStorage} from '../../global/storage/session-storage.service';
import {SessionResponse} from '../../../models/session-response.model';
import {Calculation} from '../../../models/calculation';
import {SessionDetailsResponse} from '../../../models/session-details-response.model';
import {ApplicationStateService} from '../../../../shared/services/application-state.service';
import {CalculationService} from '../calculation.service';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {RequestMetricResult} from '../../../../shared/components/models/metrics/request-metric-result.enum';
import {BusinessParamName} from '../../../../shared/models/request-offer/request-offer.enum';
import {BehaviorSubject} from 'rxjs/Rx';
import {RegistrationService} from '../registration.service';
import {StepValidationStorageService} from '../../../../shared/services/step-validation-storage.service';
import {configurationCode} from '../../../utils/utils';
import {CustomMetricsName} from '../../../../shared/components/models/metrics/custom-metrics-name.enum';
import {MetricsType} from '../../../../shared/components/models/metrics/metrics-type.enum';
import {ApplicationStateStorageService} from '../../../../shared/services/application-state-storage.service';
import {UrlParamsService} from '../url-params.service';
import {ApplicationEvent, ApplicationEventsService} from '../../../../shared/services/application-events.service';
import {CommonApiErrorHandlerService} from '../../../../shared/services/common-api-error-handler.service';
import {SalesChannel} from '../../../consts/sales-channel.enum';
import {RegistrationData} from '../../../models/registration-data.model';
import {CalculationData} from '../../../models/calculation-data.model';
import {ContactDataModel} from '../../../models/contact-data.model';
import {QueryParamName} from '../../../enums/query-param-name.enum';
import {NotificationComponentKey} from '../../../enums/components-keys';
import {ApiParamConfig} from '../../../../shared/models/api-param-config';
import {API_PARAMS_CONFIG} from '../../../configs/api-params-config';
import * as _ from 'lodash';
import {VehicleDataModel} from '../../../models/vehicle-data.model';
import {VehicleStateModel} from '../../../models/vehicle-state.model';
import {PartyTypeCode} from '../../../consts/party-type-code.enum';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  sessionId: string;
  sessionSimpleId: string;
  sessionRecreateId: string;
  uneditableSessionSubject = new BehaviorSubject<any>(null);
  canShowEntryInformationModal = false;

  private sessionEndpoint: string;
  private apiParamsConfig: ApiParamConfig[];

  private readonly CLASSNAME = 'SessionService';

  constructor(private api: RestApiService,
              private commonApiErrorHandlerService: CommonApiErrorHandlerService,
              private sessionStorage: SessionStorage,
              private stateService: ApplicationStateService,
              private applicationStorageService: ApplicationStateStorageService,
              private calculationService: CalculationService,
              private metricsService: MetricsService,
              private urlParamsService: UrlParamsService,
              private registrationService: RegistrationService,
              private eventsService: ApplicationEventsService,
              private nav: StepValidationStorageService) {
    this.metricsService.initSession(this);
    this.apiParamsConfig = API_PARAMS_CONFIG;
  }

  getSession(sessionEndpoint: string) {
    this.sessionEndpoint = sessionEndpoint;
    if (this.urlParamsService.sessionIdFromUrl) { // there is sessionId in url
      this.stateService.resetEnginePower();
      return this.getSessionByExternalId(this.urlParamsService.sessionIdFromUrl);
    } else { // there isn't sessionId in url
      const stateSessionId = this.urlParamsService.getQueryParam(QueryParamName.SESSION_ID);
      this.calculationService.dataFromSession.emit(null);
      if (stateSessionId) { // there is sessionId in storage - init calculation from storage (or raw)
        this.sessionId = stateSessionId;
        this.stateService.sessionId = this.sessionId;
        return this.getSessionDetails().then(resp => {
          if (resp.values) {
            if (resp.values.registrationProcessStarted) {
              this.uneditableSessionSubject.next(resp.values.registrationProcessStarted);
              this.cleanSessionData();
            }
          }
        });
      } else { // there is no sessionId in storage - create session
        return this.createSession();
      }
    }
  }

  getSessionById(sessionId: string) {
    this.urlParamsService.setQueryParam(QueryParamName.SESSION_ID, sessionId);
    return this.getSessionByExternalId(sessionId);
  }

  /**
   *
   * @param sendMetric - whether sessionResponse metric should be send after getting session details
   */
  getSessionDetails(sendMetric: boolean = true): Promise<any> {
    let uri = this.api.prepareUri(this.sessionEndpoint, 'attributes', 'ui');
    uri += '?sessionId=' + this.sessionId;
    uri += '&scope=SESSION_UI'; // tymczasowo, w przyszlosci bedzie doklejane na backend

    let metricName = {
      className: this.CLASSNAME,
      methodName: 'getSessionDetails'
    };

    return this.api.get(uri)
      .then((resp: SessionDetailsResponse) => {
        if (sendMetric) {
          this.sendSessionResponseMetric(resp);
        }
        return Promise.resolve(resp);
      })
      .catch(error => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);
        if (error) {
          if (error.status === 404) {
            this.sessionStorage.clear();
            this.commonApiErrorHandlerService.emitCustomError(NotificationComponentKey.SESSION_ERROR);
          } else {
            this.commonApiErrorHandlerService.emitError(error);
          }
        }
        return Promise.reject(error);
      })
  }

  handleSessionData(resp: SessionDetailsResponse): void {
    const sessionValues = resp.values;

    if (isNotNullOrUndefined(sessionValues)) {
      this.stateService.sessionDataLoaded = true;

      let data = this.prepareRegistrationAndCalculationData(sessionValues);

      if (data.calculationData && data.calculationData.hasOwnProperty('vehicle') && isNotNullOrUndefined(data.calculationData.vehicle.registrationNumber)) {
        this.stateService.state.registrationNumberFromSession = data.calculationData.vehicle.registrationNumber;
      }

      this.saveApiParamsFromSession(resp);
      this.setRegistrationState(data.registrationData);
      data = this.handleIfRegistrationStarted(sessionValues, data);

      this.stateService.state.calculationDataFromSession = isNotNullOrUndefined(data.calculationData);
      this.stateService.state.sessionCalculationData = _.cloneDeep(data.calculationData);

      if (!isNotNullOrUndefined(this.stateService.state.noDrivingLicense) && isNotNullOrUndefined(data.calculationData)) {
        this.stateService.state.noDrivingLicense = data.calculationData.owner ? !isNotNullOrUndefined(data.calculationData.owner.drivingLicenseIssueYear) : false;
      }

      this.calculationService.dataFromSession.emit(data.calculationData);
    } else {
      this.calculationService.dataFromSession.emit(null);
    }
  }

  updateSession(calculation: Calculation): void {
    this.api.put(this.sessionEndpoint, {
      id: this.sessionId,
      attributeScope: 'SESSION_UI',
      attributeValues: {
        calculation: calculation
      }
    })
  }

  createSession() {
    let metricName = {
      className: this.CLASSNAME,
      methodName: 'createSession'
    };

    return this.api.put(this.sessionEndpoint, {}, {configurationCode: configurationCode()}
    )
      .then((session: SessionResponse) => {
        this.urlParamsService.setQueryParam(QueryParamName.SESSION_ID, session.id);
        this.sessionId = session.id;
        this.stateService.sessionId = this.sessionId;
        this.sessionSimpleId = session.simpleId;
        this.canShowEntryInformationModal = true;
        this.stateService.saveBusinessParameter(BusinessParamName.SESSION_SIMPLE_ID, session.simpleId);
        this.stateService.saveBusinessParameter(BusinessParamName.ORDER_ID, null); // reset Order-Id
        this.eventsService.sendEvent(ApplicationEvent.INIT_SESSION);
        return Promise.resolve();
      })
      .catch(error => {
        this.metricsService.sendRequestMetric({
          ...metricName,
          type: RequestMetricResult.ERROR
        }, error);
        return Promise.reject(error);
      });
  }

  async resetSession() {
    await this.createSession();
    this.stateService.saveBusinessParameter(BusinessParamName.ORDER_ID, null); // reset Order-Id
  }

  getBusinessParameter(resp: SessionDetailsResponse, paramName: BusinessParamName): any {
    if (resp.values.header) {
      const businessParams = resp.values.header.processContext.businessParameters;
      const result = businessParams.filter(param => param.parameterName === paramName);
      if (result.length > 0) {
        return result[result.length - 1].parameterValue;
      }
    }
    return null;
  }

  cleanSessionData(): void {
    this.stateService.state.calculationDataFromSession = false;
    this.calculationService.dataFromSession.emit(null);
    this.stateService.previewSectionMode$.next(false);
    this.removeOfferId();
    this.registrationService.resetOfferRegistrationData(false);
    this.calculationService.createCalculationDataAndState();
    this.canShowEntryInformationModal = false;
    this.nav.clearSteps();
  }

  private getSessionByExternalId(sessionId: string) {
    this.sessionId = sessionId;
    this.sessionRecreateId = sessionId;
    this.stateService.sessionId = this.sessionId;
    this.stateService.state.entryWithSessionId = true;
    this.stateService.state.usedPreviewMode = true;
    this.applicationStorageService.updateApplicationState();
    this.stateService.previewSectionMode$.next(true);

    return this.getSessionDetails().then(resp => {
      const isSessionRecreated = this.urlParamsService.getActiveQueryParam(QueryParamName.SESSION_STAGE) === 'recreate';

      if (isSessionRecreated && resp.values.registrationProcessStarted) {
        const copySessionData: { calculationData: CalculationData, registrationData: RegistrationData } = this.prepareRegistrationAndCalculationData(resp.values);
        this.saveApiParamsInRecreatedSession(resp);

        return this.createSession().then(() => {
          this.stateService.saveBusinessParameter(BusinessParamName.SESSION_RECREATE_ID, this.sessionRecreateId);
          this.stateService.saveBusinessParameter(BusinessParamName.SESSION_STAGE, 'RECREATE');
          this.stateService.state.usedPreviewMode = true;
          this.stateService.previewSectionMode$.next(true);
          this.calculationService.dataFromSession.emit(copySessionData.calculationData);
          return resp;
        });
      } else {
        this.handleSessionData(resp);
        return resp;
      }
    });
  }

  private saveApiParamsFromSession(resp: SessionDetailsResponse): void {
    this.apiParamsConfig
        .filter(config => config.saveFromSession)
        .forEach(paramConfig => this.saveBusinessParam(paramConfig.paramName as BusinessParamName, resp));
  }

  private saveApiParamsInRecreatedSession(resp: SessionDetailsResponse): void {
    this.apiParamsConfig
        .filter(config => config.saveInRecreatedSession)
        .forEach(paramConfig => this.saveBusinessParam(paramConfig.paramName as BusinessParamName, resp));
  }

  private saveBusinessParam(paramName: BusinessParamName, resp: SessionDetailsResponse): void {
    const paramValue = this.getBusinessParameter(resp, paramName);
    if (isNotNullOrUndefined(paramValue)) {
      this.stateService.saveBusinessParameter(paramName, paramValue);
    }

    switch (paramName) {
      case BusinessParamName.SESSION_SIMPLE_ID:
        this.sessionSimpleId = paramValue;
        break;
      case BusinessParamName.APPLICATION_ID:
        if (isNotNullOrUndefined(paramValue) && this.stateService.salesChannel === SalesChannel.BANKIER) {
          this.stateService.bankierIdSalesChannelBankier = paramValue;
        }
        break;
      case BusinessParamName.SELECTED_USER_FILTERS:
        this.stateService.saveSelectedUserFilters(paramValue);
        break;
      case BusinessParamName.ANALYTICS_USER_ID:
        if (!isNotNullOrUndefined(paramValue) && resp.values) {
          const data = this.prepareRegistrationAndCalculationData(resp.values);
          if (data.calculationData && data.calculationData.contact && data.calculationData.contact.email) {
            this.stateService.createAnalyticsUserId(data.calculationData.contact.email);
          }
        }
    }
  }

  private removeOfferId(): void {
    delete this.stateService.state.queryParams.offerId;
  }

  private sendSessionResponseMetric(sessionResponse: SessionDetailsResponse): void {
    const value = JSON.stringify(sessionResponse);
    this.metricsService.sendSimpleMetric(CustomMetricsName.SESSION_RESPONSE, MetricsType.RESPONSE, value);
  }

  private setRegistrationState(data: any) {
    const dataFromSessionIncluded = isNotNullOrUndefined(this.stateService.registrationState) ?
      this.stateService.registrationState.dataFromSessionIncluded : false;
    this.stateService.registrationState = {
      registrationFromSession: data,
      dataFromSessionIncluded: dataFromSessionIncluded
    };
  }

  private prepareRegistrationAndCalculationData(sessionValues) {
    let registrationData, calculationData: CalculationData;
    try {
      calculationData = sessionValues.body.calculationData;

      if (!isNotNullOrUndefined(calculationData.owner.partyTypeCode)) {
        calculationData.owner.partyTypeCode = isNotNullOrUndefined(calculationData.owner.companyName) ? PartyTypeCode.BUSINESS_ACTIVITY : PartyTypeCode.NATURAL_PERSON;
      }

      if (!isNotNullOrUndefined(calculationData.vehicle)) {
        calculationData.vehicle = new VehicleDataModel();
      }
      if (!isNotNullOrUndefined(calculationData.vehicleState)) {
        calculationData.vehicleState = new VehicleStateModel();
      }
      if (!isNotNullOrUndefined(calculationData.insurer)) {
        calculationData.insurer = calculationData.owner;
      }
      if (!isNotNullOrUndefined(calculationData.insurer.partyTypeCode)) {
        calculationData.insurer.partyTypeCode = isNotNullOrUndefined(calculationData.insurer.companyName) ? PartyTypeCode.BUSINESS_ACTIVITY : PartyTypeCode.NATURAL_PERSON;
      }
      if (!isNotNullOrUndefined(calculationData.contact)) {
        calculationData.contact = new ContactDataModel();
      }
      if (sessionValues.body && sessionValues.body.registrationData) {
        registrationData = sessionValues.body.registrationData;
      } else {
        registrationData = sessionValues.registration.body.registrationData;
      }
    } catch (e) {
      calculationData = calculationData ? calculationData : null;
      registrationData = registrationData ? registrationData : null;
    }
    return {registrationData, calculationData};
  }

  private handleIfRegistrationStarted(sessionValues, data) {
    if (isNotNullOrUndefined(sessionValues.registrationProcessStarted)) {
      this.uneditableSessionSubject.next(sessionValues.registrationProcessStarted);
      if (sessionValues.registrationProcessStarted) {
        this.removeOfferId();
        this.stateService.state.usedPreviewMode = false;
        this.stateService.previewSectionMode$.next(false);
        return {registrationData: null, calculationData: null};
      } else {
        return data;
      }
    } else {
      this.canShowEntryInformationModal = true;
      return data;
    }
  }
}

